import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from 'react';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import { FieldError } from '@/ui';
import { Field } from 'react-final-form';
import { FixedSizeList } from 'react-window';
import debounce from 'lodash/debounce';
import SearchLabel from '../../../components/SearchLabel/SearchLabel';
import DraggableOffer from './DraggableOffer';
import ZuulLoader from '../../../components/ZuulLoader/ZuulLoader';
import './LeftAside.css';

const LABEL_SX = { display: 'block', mb: 0.5 };

const NameField = memo(function NameField({ input, disabled, type, placeholder, meta: { touched, error } }) {
  return (
    <>
      <TextField {...input} id='offerBlockName' disabled={disabled} placeholder={placeholder} type={type} error={Boolean(touched && error)} />
      <FieldError error={error} touched={touched} />
    </>
  );
});

const VirtualRow = memo(function VirtualRow({ index, style, data }) {
  const offer = data[index];

  if (!offer) {
    return <div style={style}>Loading...</div>;
  }

  return (
    <div style={style}>
      <div
        style={{
          height: '65px',
          paddingBottom: '5px',
          overflow: 'hidden',
        }}
      >
        <DraggableOffer offer={offer} />
      </div>
    </div>
  );
});

const LeftAside = memo(function LeftAside({ offers, areOffersLoading }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [listHeight, setListHeight] = useState(0);
  const poolRef = useRef(null);
  const listRef = useRef(null);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setDebouncedSearchTerm(value);
    }, 300),
    []
  );

  // The virtual list needs an explicit pixel height, and it has to be exactly the pool's
  // content box. react-window decides whether to show a scrollbar by comparing the rows'
  // total height against this number, so a list taller than the pool is the worst case:
  // the pool (overflow: hidden) clips the surplus, and when the rows fit inside the
  // list's own viewport there is no scrollbar to reach them with. A floor of 300px used
  // to guarantee exactly that whenever the aside was shorter than ~320px — a short
  // browser window hid the last rows of a short list with no way to scroll to them.
  //
  // A ResizeObserver, not a window resize listener: the pool also changes height on its
  // own — a validation message appearing above it, the loader giving way to the list —
  // and none of those fire a window resize.
  useLayoutEffect(() => {
    const pool = poolRef.current;
    if (!pool) return undefined;

    const measure = () => {
      const { paddingTop, paddingBottom } = window.getComputedStyle(pool);
      const padding = parseFloat(paddingTop) + parseFloat(paddingBottom);
      setListHeight(Math.max(0, pool.clientHeight - padding));
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(pool);
    return () => observer.disconnect();
  }, []);

  // A re-filtered list starts at the top. react-window keeps its scroll offset when the
  // rows underneath it change, so typing a search used to drop you part-way down a list
  // whose beginning you had never seen.
  useEffect(() => {
    listRef.current?.scrollTo(0);
  }, [debouncedSearchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const draggableOffers = useMemo(() => {
    if (!offers || offers.length === 0) return [];

    if (!debouncedSearchTerm) return offers;

    const searchLower = debouncedSearchTerm.toLowerCase();

    return offers.filter((offer) => {
      if (!offer) return false;

      const matchesSearch =
        offer.name?.toLowerCase().includes(searchLower) || offer.id?.toString().includes(debouncedSearchTerm);

      return matchesSearch;
    });
  }, [offers, debouncedSearchTerm]);

  return (
    <div className='left-aside-container'>
      <div>
        <FormLabel htmlFor='offerBlockName' sx={LABEL_SX}>
          Offer Block Name
        </FormLabel>
        <Field name='offer.name'>
          {({ input, meta: { touched, error } }) => (
            <NameField input={input} type='text' placeholder='Offer Block Name' meta={{ touched, error }} />
          )}
        </Field>
      </div>
      <div>
        <SearchLabel />
        <TextField
          type='text'
          placeholder='Use spaces to separate search terms'
          onChange={handleSearchChange}
          value={searchTerm}
        />
      </div>

      <div className='draggableOffersPool' ref={poolRef}>
          {areOffersLoading ? (
            <ZuulLoader text={'Loading Offers...'} />
          ) : draggableOffers.length > 0 ? (
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              itemCount={draggableOffers.length}
              itemSize={70}
              width='100%'
              overscanCount={5}
              itemData={draggableOffers}
            >
              {VirtualRow}
            </FixedSizeList>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center' }}>No offers found</div>
        )}
      </div>
    </div>
  );
});

export default LeftAside;
